/**
 * Bluedot Order Placement Integration
 *
 * Handles order placement workflow:
 * 1. Send Bluedot wave with order data
 * 2. Start location tracking
 * 3. Emit postMessage for webview communication
 */

import bluedotService from "./bluedot";
import { isWebview } from "../utils/isWebview";
import { requestBluedotLocation } from "../utils/bluedotLocation";

// Message type constant
export const MESSAGE_TYPE = "APPFRONT_ORDER_PLACED";

/**
 * Send order placement postMessage to native app
 *
 * @param {Object} orderData - Order information
 * @returns {boolean}
 */
const sendPostMessage = orderData => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const message = {
      type: MESSAGE_TYPE,
      timestamp: new Date().toISOString(),
      payload: {
        orderId: orderData.orderId,
        customerId: orderData.customerId,
        orderTotal: orderData.orderTotal,
        currency: orderData.currency || "USD",
        restaurantName: orderData.restaurantName,
        bluedotDestinationId: orderData.bluedotDestinationId,
        destinationId: orderData.bluedotDestinationId,
        customerPhone: orderData.customerPhone || "",
        customerPhoneNumber: orderData.customerPhoneNumber || orderData.customerPhone || "",
        mandatoryTextFields: (orderData.mandatoryTextFields || []).filter(
          ({ value }) => value != null && String(value).trim() !== ""
        ),
        locationEnabled: false // Will be updated after location request
      }
    };

    if (typeof window.postMessage === "function") {
      window.postMessage(JSON.stringify(message), "*");
      return true;
    }
  } catch (error) {
    console.error("[Bluedot] postMessage error:", error);
  }

  return false;
};

/**
 * Update native app with location status
 *
 * @param {boolean} locationEnabled - Whether location is enabled
 */
const updateLocationStatus = locationEnabled => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const message = {
      type: "APPFRONT_BLUEDOT_LOCATION_STATUS",
      timestamp: new Date().toISOString(),
      payload: {
        locationEnabled
      }
    };

    if (typeof window.postMessage === "function") {
      window.postMessage(JSON.stringify(message), "*");
    }
  } catch (error) {
    console.error("[Bluedot] Location status update error:", error);
  }
};

/**
 * Send order placement wave to Bluedot
 *
 * @param {Object} orderData - Order information
 * @returns {Promise<Object>} - Result object
 */
const sendOrderWave = async orderData => {
  const waveData = {
    eventType: "orderPlaced",
    orderId: orderData.orderId,
    customerId: orderData.customerId,
    customerPhoneNumber: orderData.customerPhoneNumber || orderData.customerPhone || "",
    orderTotal: orderData.orderTotal,
    currency: orderData.currency || "USD",
    restaurantName: orderData.restaurantName,
    orderType: orderData.orderType || "delivery",
    paymentMethod: orderData.paymentMethod || "unknown",
    timestamp: new Date().toISOString()
  };

  if (Array.isArray(orderData.mandatoryTextFields)) {
    orderData.mandatoryTextFields.forEach(({ field, value }) => {
      if (field && value != null && String(value).trim() !== "")
        waveData[field] = String(value);
    });
  }

  return await bluedotService.sendWave(
    orderData.bluedotDestinationId,
    waveData
  );
};

/**
 * Handle order placement complete
 *
 * This is the main entry point for order placement Bluedot integration.
 * It should be called when an order is successfully placed.
 *
 * IMPORTANT: When running inside a native WebView, we do NOT use the Web SDK
 * to send waves or track location. Instead, we send a postMessage to the
 * native app, which uses the Point SDK's Geo-Triggering service
 * (geoTriggeringService.start) for background location tracking and zone
 * detection. The Web SDK is only used in standalone browser mode.
 *
 * @param {Object} orderData - Order information
 * @returns {Promise<Object>} - Result object with wave and postMessage status
 */
export const handleOrderPlaced = async orderData => {
  const result = {
    success: false,
    waveSent: false,
    postMessageSent: false,
    locationEnabled: false,
    delegatedToNative: false,
    error: null,
    orderId: orderData ? orderData.orderId : null
  };

  try {
    // Validate order data
    if (!orderData || !orderData.orderId) {
      result.error = "Invalid order data";
      return result;
    }

    // --- WebView / Native App flow ---
    // Delegate entirely to native app which will use Geo-Triggering service
    // (Point SDK) for background location tracking. Do NOT use Web SDK here.
    if (isWebview()) {
      console.log(
        "[Bluedot] Running in WebView - delegating to native Geo-Triggering"
      );
      result.postMessageSent = sendPostMessage(orderData);
      result.delegatedToNative = true;
      result.success = result.postMessageSent;
      return result;
    }

    // --- Standalone browser flow ---
    // Use Web SDK to send wave and enable location tracking

    // Send wave in background (don't block order flow)
    const wavePromise = sendOrderWave(orderData);

    // Wait for wave to complete
    const waveResult = await wavePromise;
    result.waveSent = !waveResult || !waveResult.error;
    result.success = result.waveSent;

    // Start location tracking after wave (browser only)
    try {
      const locationResult = await requestBluedotLocation();
      result.locationEnabled = locationResult;
    } catch (locationError) {
      console.error(
        "[Bluedot] Location tracking error:",
        locationError.message
      );
      // Don't fail the whole operation for location errors
    }
  } catch (error) {
    console.error("[Bluedot] Order placement error:", error);
    result.error = error.message || "Unknown error";
  }

  return result;
};

/**
 * Quick wave send for order without full location flow
 * Use this when you only need to send a wave (standalone browser only).
 * In WebView, location updates are handled by the native Geo-Triggering service.
 *
 * @param {Object} orderData - Order information
 * @returns {Promise<Object>} - Wave result
 */
export const sendQuickWave = async orderData => {
  if (isWebview()) {
    console.log(
      "[Bluedot] sendQuickWave skipped - native Geo-Triggering handles waves"
    );
    return { skipped: true, reason: "webview" };
  }

  if (!orderData || !orderData.orderId) {
    return { error: "Invalid order data" };
  }

  const waveData = {
    eventType: "orderPlaced",
    orderId: orderData.orderId,
    customerId: orderData.customerId,
    customerPhoneNumber: orderData.customerPhoneNumber || orderData.customerPhone || "",
    timestamp: new Date().toISOString()
  };

  if (Array.isArray(orderData.mandatoryTextFields)) {
    orderData.mandatoryTextFields.forEach(({ field, value }) => {
      if (field && value != null && String(value).trim() !== "")
        waveData[field] = String(value);
    });
  }

  return await bluedotService.sendWave(
    orderData.bluedotDestinationId,
    waveData
  );
};

/**
 * Send location update wave (standalone browser only).
 * In WebView, location updates are handled by the native Geo-Triggering service.
 *
 * @param {Object} locationData - Location information
 * @returns {Promise<Object>} - Wave result
 */
export const sendLocationUpdate = async locationData => {
  if (isWebview()) {
    console.log(
      "[Bluedot] sendLocationUpdate skipped - native Geo-Triggering handles location"
    );
    return { skipped: true, reason: "webview" };
  }

  const waveData = {
    eventType: "locationUpdate",
    orderId: locationData.orderId,
    latitude: locationData.latitude,
    longitude: locationData.longitude,
    accuracy: locationData.accuracy,
    timestamp: new Date().toISOString()
  };

  return await bluedotService.sendWave(
    locationData.bluedotDestinationId,
    waveData
  );
};

export default {
  MESSAGE_TYPE,
  handleOrderPlaced,
  sendQuickWave,
  sendLocationUpdate
};
